'use client';

import Link from "next/link";
import { identity, telHref } from "@/content/identity";
import { useTranslations } from "@/i18n/client";

type FooterItem = { label: string; href?: string };
type FooterColumn = { title: string; items: FooterItem[] };

/** 4-column footer — mirrors the real codiaj.com footer structure. */
export default function Footer() {
  const { t } = useTranslations();
  const footer = t('footer');

  const columns: FooterColumn[] = footer.columns
    ? [
        {
          title: footer.columns.nav.title,
          items: [
            { label: footer.columns.nav.items.inicio, href: "/" },
            { label: footer.columns.nav.items.servicios, href: "/servicios" },
            { label: footer.columns.nav.items.casos, href: "/casos-exito" },
            { label: footer.columns.nav.items.acerca, href: "/acerca-de" },
            { label: footer.columns.nav.items.contacto, href: "/contacto" },
            { label: footer.columns.nav.items.blog, href: "/blog" },
          ],
        },
        {
          title: footer.columns.contacto.title,
          items: [
            ...identity.telefonos.map((tel) => ({ label: tel, href: telHref(tel) })),
            { label: identity.email, href: `mailto:${identity.email}` },
            { label: identity.direccion },
          ],
        },
        {
          title: footer.columns.legal.title,
          items: [
            { label: footer.columns.legal.items.avisoLegal, href: "/aviso-legal" },
            { label: footer.columns.legal.items.consentimiento, href: "/consentimiento-clientes" },
            { label: footer.columns.legal.items.accesibilidad, href: "/accesibilidad" },
            { label: footer.columns.legal.items.reembolso, href: "/politica-reembolso" },
            { label: footer.columns.legal.items.privacidad, href: "/politica-privacidad" },
            { label: footer.columns.legal.items.terminos, href: "/terminos-condiciones" },
            { label: footer.columns.legal.items.cookies, href: "/politica-de-cookies" },
          ],
        },
      ]
    : [];

  return (
    <footer className="bg-[var(--color-surface)] border-t border-[var(--color-border)] pt-[72px] pb-14 mt-auto">
      <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 pb-12 border-b border-[var(--color-border)]">
          <div>
            <Link href="/" className="flex items-center no-underline mb-6">
              <img 
                src="/brand/logo-text.png" 
                alt="Codiaj" 
                className="h-8 w-auto object-contain block" 
              />
            </Link>
            <p className="font-sans text-[13px] text-[var(--color-mute)] mt-4 max-w-[280px] leading-[1.6]">
              {footer.tagline || identity.lema}
            </p>
            <p className="font-mono text-[10px] tracking-widest uppercase text-[var(--color-mute)] mt-6">
              {identity.ubicacion}
            </p>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--color-ink)] mb-6">
                {col.title}
              </div>
              {col.items.map((item) => (
                <div
                  key={item.label}
                  className="font-sans text-[14px] text-[var(--color-mute)] mb-3 leading-[1.5]"
                >
                  {item.href ? (
                    <div className="flex items-center gap-2">
                      {item.href.startsWith("tel:") && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-40"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      )}
                      {item.href.startsWith("mailto:") && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-40"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                      )}
                      {item.href.startsWith("/") ? (
                        <Link
                          href={item.href}
                          className="no-underline transition-colors duration-150 hover:text-[var(--color-ink)]"
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <a
                          href={item.href}
                          className="no-underline transition-colors duration-150 hover:text-[var(--color-ink)]"
                        >
                          {item.label}
                        </a>
                      )}
                    </div>
                  ) : (
                    item.label
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row justify-between mt-10 gap-4 font-mono text-[10px] tracking-widest uppercase text-[var(--color-mute)]">
          <div>{footer.copyright || '© 2026 · Codiaj Madrid'}</div>
          <div>{footer.madeBy || 'Hecho a mano · sin adelantos'}</div>
        </div>
      </div>
    </footer>
  );
}
